import { ChangeDetectorRef } from "@angular/core";
import { Observable, Subject, animationFrameScheduler, merge } from "rxjs";
import { auditTime, filter, tap, debounceTime } from "rxjs/operators";

/** default iterations control */
const _oscillationThreshold = 2; // How many size alternations before considering oscillation
const _minStepSize = 1; // Minimum pixel change to consider
const _dampingFactor = 0.5; // Reduce step size when oscillating
const _maxIterations = 10; // Safety limit for iterations
const _pauseLength = 500; // wait ms after last resize event before resuming
const RESIZE_LIMITS_DEFAULT = {
        oscillationThreshold: _oscillationThreshold, // How many size alternations before considering oscillation
        minStepSize: _minStepSize, // Reduce step size when oscillating
        dampingFactor: _dampingFactor,
        maxIterations: _maxIterations,
        pauseLength: _pauseLength,
    };
export const getMinMax = (el: HTMLElement) => {
    const style = getComputedStyle(el);
    // any non-numeric value treated as 0
    const maxWidth = parseInt(`0${style.maxWidth}`);
    const minWidth = parseInt(`0${style.minWidth}`);
    return { minWidth, maxWidth };
}
export interface ResizeLimits {
    oscillationThreshold: number;
    minStepSize: number;
    dampingFactor: number;
    maxIterations: number;
    pauseLength: number;
}
/** convergent iterations */
export class IterationOscillationDamper {
    // copy: init() overrides must not change the defaults of other instances
    protected limits: ResizeLimits = { ...RESIZE_LIMITS_DEFAULT }; // default limits, can be overridden by init

    /** converge resizing */
    protected previousSizes = [] as number[]; 
    protected lastSize = 0;
    protected iterationCount = 0;
    protected oscillationCount = 0; // consecutive oscillating steps, damping grows with each
    public nextUpdate = 0; // run updates no earlier than; use when stopping the iterations

    /** check this before calculation, used to pause */
    get canCalculate() {
        return this.nextUpdate < Date.now();
    }
    
    /** override default limits. */
    public init(limits?: Partial<ResizeLimits>) {
        if (limits) {
            Object.assign(this.limits, limits);
        }
    }
    public calculateNextSize(currentSize: number, requiredChange: number, minimumSize: number, canStop: boolean, useMax = true) {
        this.iterationCount++;

        // Check if we've reached max iterations
        if (this.iterationCount >= this.limits.maxIterations && canStop) {
            this.stopResizeIteration();
            // return maximum of 2 last iterations and minimumSize
            return Math.max(useMax ? Math.max(currentSize, this.lastSize) : Math.min(currentSize, this.lastSize), minimumSize);
        }

        // Store current size
        this.lastSize = currentSize;
        this.previousSizes.push(currentSize);

        // Check for oscillation (alternating between increasing and decreasing)
        if (this.isOscillating()) {
            // Dampen the change, more with each consecutive oscillation (converges geometrically)
            this.oscillationCount++;
            requiredChange *= Math.pow(this.limits.dampingFactor, this.oscillationCount);
        } else {
            this.oscillationCount = 0;
        }

        const size = Math.max(currentSize + requiredChange, minimumSize);
        // If change becomes too small, stop
        if (Math.abs(size - currentSize) < this.limits.minStepSize) {
            this.stopResizeIteration();
            return size;
        } else {
            return size;
        }
    }
    protected stopResizeIteration() {
        this.nextUpdate = Date.now() + this.limits.pauseLength;

        setTimeout(() => {
            this.reset();
            this.onReset();
        }); // reset iterations state
    }

    /** oscillating = the last `oscillationThreshold` size changes alternate direction (grow, shrink, grow...)
     * (previously a single change already counted as oscillation, so every step was damped) */
    private isOscillating() {
        const threshold = Math.max(2, this.limits.oscillationThreshold);
        // need threshold changes, i.e. threshold + 1 sizes
        if (this.previousSizes.length < threshold + 1) {
            return false;
        }
        const recentChanges: number[] = [];
        for (let i = this.previousSizes.length - threshold; i < this.previousSizes.length; i++) {
            recentChanges.push(Math.sign(this.previousSizes[i] - this.previousSizes[i - 1]));
        }
        // no change is not an alternation
        if (recentChanges.includes(0)) {
            return false;
        }
        // signs must alternate
        return recentChanges.every((sign, i) => i === 0 || sign !== recentChanges[i - 1]);
    }
    protected onReset(): void { };

    public reset() {
        this.previousSizes = [];
        this.iterationCount = 0;
        this.lastSize = 0;
        this.oscillationCount = 0;
    }
}
/** resizable container */
export abstract class ResizableContainer extends IterationOscillationDamper {
    /** inject on creation */
    protected abstract rootElement: HTMLElement;  // use to set custom CSS variables
    protected abstract resizableElement: HTMLElement;  // resizable element (can be the same as root unless have more pieces such as background)
    protected abstract cdr: ChangeDetectorRef;
    protected abstract resizeObserver: ResizeObserver;
    protected abstract stateResize: Observable<ResizeObserverEntry[]>;
    protected abstract calculateSize(): void;

    /** optional trigger */
    public triggerCalculation?: Observable<void>; // assign before init if necessary

    override get canCalculate() {
        return this.resizableElement && super.canCalculate;
    }
    public updated = new Subject<void>();
    public updateDelay = 20; // ms after last resize event we announce update

    public override init(limits?: Partial<ResizeLimits>) {
        super.init(limits);
        const triggers = [
            /** this can trigger on unrelated resize, so filter */
            this.stateResize
                .pipe(
                    filter((entries: ResizeObserverEntry[]) => this.canCalculate && entries.some(e => e.target === this.resizableElement)),
                ) as Observable<any>
        ];
        /** use other trigger than resize */
        if (this.triggerCalculation) {
            triggers.push(this.triggerCalculation);
        }
        merge(...triggers).pipe(
            /** do not resize inside the ResizeObserver callback (causes "ResizeObserver loop completed with undelivered notifications"),
             * run once in the next animation frame instead (also coalesces multiple triggers in the same frame) */
            auditTime(0, animationFrameScheduler),
            filter(() => this.canCalculate), // re-check, iterations could be paused in the meantime
            tap(() => this.calculateSize()),
            debounceTime(this.updateDelay))
            .subscribe(() => {
                this.updated.next();
            });
        this.resizeObserver.observe(this.resizableElement);
    }
    protected adjustSize(currentSize: number, increaseBy: number, decreaseBy: number, canStopIterations = true, minimumSize = 0) {
        let requiredChange = 0;

        // Your logic to determine required change based on conditions
        if (increaseBy > 0) {
            requiredChange = increaseBy;
        }
        /** if we have contradictory inputs, we might strecth by ones and shrink by others */
        if (decreaseBy > 0) {
            requiredChange = -decreaseBy;
        }

        return this.calculateNextSize(currentSize, requiredChange, minimumSize, canStopIterations); // cannot stop when overflow
    }

    protected override onReset(): void {
        this.cdr.detectChanges();
    };


    public recalculate() {
        this.reset();
        if (this.canCalculate) this.calculateSize();
    }

}
/** resize popup container, from min/max width and aspect ratio */
export abstract class ResizablePopupContainer extends ResizableContainer {

    // following are popup specific variables
    public aspectRatio: number = 5 / 4;
    public shrinkHeightToContent: boolean = false;
    protected lastWidth = 0; // last width of the dialog
    protected lastTargetWidth = 0; // last calculated width of the dialog
    public dialogDimensions = ''; // debug parameter to show the sizing

    /** uses aspect ratio to calculate target height */
    protected updateGetHeight(targetWidth: number): number {
        targetWidth = Math.ceil(targetWidth);
        this.lastTargetWidth = targetWidth;
        this.rootElement.style.setProperty('--w-dialog-width', `${targetWidth}px`);
        return Math.ceil(targetWidth / this.aspectRatio);
    }

    protected override onReset() {
        super.onReset();
        // create size/aspect info, used for debugging
        const dialog = this.resizableElement.getBoundingClientRect();
        const currentWidth = Math.ceil(dialog.width);
        const currentHeight = Math.ceil(dialog.height);
        Promise.resolve().then(() => {
            this.dialogDimensions = `${currentHeight}x${currentWidth} [${this.rootElement.style.getPropertyValue('--w-dialog-height')} x ${this.rootElement.style.getPropertyValue('--w-dialog-width')}] aspect: ${(currentWidth / currentHeight).toFixed(2)} [${this.aspectRatio}]`;
        });
    }
    
    public override reset() {
        super.reset();
        this.lastTargetWidth = 0;
        this.lastWidth = 0;
    }
}
const rectKeys = ['bottom', 'height', 'left', 'right', 'top', 'width', 'x', 'y'];
export const isEqualRect = (a: Record<string, any> | undefined, b: Record<string, any> | undefined) => {
    if (a && b) {
        return rectKeys.every((k: string) => a[k] === b[k]);
    } else {
        return !!(a || b) || a === b;
    }
}
